import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Stack,
  ScrollArea,
  Text,
  Group,
  useMantineColorScheme,
  Pagination,
  TextInput,
  Loader,
  Center,
  Alert,
  Select,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconAlertCircle } from "@tabler/icons-react";
import UserProfileCard from "components/UserProfileCard";
import { useNavigate } from "react-router-dom";
import { useListUsersQuery, UserFilterInput, SortEnumType } from "generated/graphql";

/** Default number of items per page */
const DEFAULT_PAGE_SIZE = 20;

/** Available page size options */
const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10 per page" },
  { value: "20", label: "20 per page" },
  { value: "50", label: "50 per page" },
];

/**
 * UsersOverview
 *
 * Displays a paginated, searchable list of users using real GraphQL data.
 * Features:
 * - Server-side pagination with skip/take
 * - Debounced search filtering by name
 * - Loading, error, and empty state handling
 * - Theme-aware styling
 *
 * @returns {JSX.Element} The UsersOverview page component
 */
export default function UsersOverview() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);

  // Build filter based on search term
  const where: UserFilterInput | undefined = useMemo(() => {
    if (!debouncedSearch.trim()) return undefined;
    return {
      name: { contains: debouncedSearch.trim() },
    };
  }, [debouncedSearch]);

  // Reset to page 1 when search changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((value: string | null) => {
    if (value) {
      setPageSize(parseInt(value, 10));
      setPage(1);
    }
  }, []);

  // GraphQL query with pagination and filtering
  const { data, loading, error } = useListUsersQuery({
    variables: {
      skip: (page - 1) * pageSize,
      take: pageSize,
      where,
      order: [{ name: SortEnumType.Asc }],
    },
    fetchPolicy: "cache-and-network",
  });

  const users = data?.users?.items ?? [];
  const totalCount = data?.users?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Handle page change with bounds checking
  const handlePageChange = useCallback(
    (newPage: number) => {
      const safePage = Math.min(Math.max(1, newPage), totalPages);
      setPage(safePage);
    },
    [totalPages]
  );

  /**
   * Helper to extract display roles from the user data.
   * Filters out null values and returns an array of strings.
   */
  const getRolesDisplay = (
    roles: Array<string | null> | null | undefined,
    roleNames: Array<string> | null | undefined
  ): string[] => {
    // Prefer roleNames if available (typically display-friendly names)
    if (roleNames && roleNames.length > 0) {
      return roleNames;
    }
    // Fall back to roles, filtering out nulls
    if (roles && roles.length > 0) {
      return roles.filter((r): r is string => r !== null);
    }
    return [];
  };

  return (
    <Container fluid p={{ base: "sm", sm: "md" }}>
      <Stack gap="md">
        {/* Header with search and page size controls */}
        <Stack gap="sm">
          <Text fw={700} size="lg">
            Users
          </Text>
          {/* Search and filter controls - stack on mobile, row on larger screens */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
            <TextInput
              placeholder="Search users..."
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.currentTarget.value)}
              className="w-full sm:w-56"
            />
            <Select
              data={PAGE_SIZE_OPTIONS}
              value={pageSize.toString()}
              onChange={handlePageSizeChange}
              className="w-full sm:w-32"
              size="sm"
            />
          </div>
        </Stack>

        {/* Loading state */}
        {loading && users.length === 0 && (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        )}

        {/* Error state */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading users"
            color="red"
            variant={isDark ? "filled" : "light"}
          >
            {error.message}
          </Alert>
        )}

        {/* Content */}
        {!loading || users.length > 0 ? (
          <>
            <ScrollArea.Autosize mah="70vh">
              <Stack gap="md">
                {users.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => navigate(`/users/${user.id}`)}
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(`/users/${user.id}`);
                      }
                    }}
                  >
                    <UserProfileCard
                      variant="compact"
                      avatarUrl={user.avatarUrl ?? undefined}
                      name={user.name}
                      roles={getRolesDisplay(user.roles, user.roleNames)}
                    />
                  </div>
                ))}
                {/* Empty state */}
                {!loading && users.length === 0 && !error && (
                  <Center py="xl">
                    <Text c={isDark ? undefined : "dimmed"}>
                      {debouncedSearch
                        ? `No users found matching "${debouncedSearch}"`
                        : "No users to display."}
                    </Text>
                  </Center>
                )}
              </Stack>
            </ScrollArea.Autosize>

            {/* Pagination controls - stack on mobile */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-4">
                <Text size="sm" c="dimmed" className="text-center sm:text-left">
                  Showing {Math.min((page - 1) * pageSize + 1, totalCount)} -{" "}
                  {Math.min(page * pageSize, totalCount)} of {totalCount} users
                </Text>
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={handlePageChange}
                  size="sm"
                  className="justify-center sm:justify-end"
                />
              </div>
            )}
          </>
        ) : null}
      </Stack>
    </Container>
  );
}
